import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  CreateRepairPayload,
  RepairOrder,
  RepairPaymentPayload,
  RepairStatus,
  RepairsApiService,
} from '@adminpages/repairs-api';
import { PosWorkspaceService } from '@adminpages/pos-state';

@Injectable({ providedIn: 'root' })
export class RepairsStoreService {
  private readonly api = inject(RepairsApiService);
  private readonly workspace = inject(PosWorkspaceService);

  readonly repairs = signal<RepairOrder[]>([]);
  readonly selectedRepair = signal<RepairOrder | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  async load(params: { q?: string; status?: RepairStatus } = {}): Promise<void> {
    this.errorMessage.set(null);
    this.loading.set(true);

    try {
      this.repairs.set(await this.api.repairs(this.workspace.selectedWorkspace().id, params));
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
    } finally {
      this.loading.set(false);
    }
  }

  async loadOne(uuid: string): Promise<RepairOrder> {
    this.errorMessage.set(null);
    this.loading.set(true);

    if (this.selectedRepair()?.uuid !== uuid) {
      this.selectedRepair.set(null);
    }

    try {
      const repair = await this.api.repair(this.workspace.selectedWorkspace().id, uuid);
      this.setRepair(repair);
      return repair;
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  async create(payload: CreateRepairPayload): Promise<RepairOrder> {
    return this.save(async () => this.api.create(this.workspace.selectedWorkspace().id, payload));
  }

  async updateTechnicalData(uuid: string, payload: Record<string, unknown>): Promise<RepairOrder> {
    return this.save(async () => this.api.updateTechnicalData(this.workspace.selectedWorkspace().id, uuid, payload));
  }

  async transition(uuid: string, status: RepairStatus, payload: Record<string, unknown> = {}): Promise<RepairOrder> {
    return this.save(async () => this.api.transition(this.workspace.selectedWorkspace().id, uuid, status, payload));
  }

  async addPayment(uuid: string, payload: RepairPaymentPayload): Promise<RepairOrder> {
    return this.save(async () => this.api.addPayment(this.workspace.selectedWorkspace().id, uuid, payload));
  }

  async uploadAttachment(
    uuid: string,
    file: File,
    data: { type: string; visibility: 'internal' | 'customer'; includeInDocuments: boolean; title?: string },
  ): Promise<void> {
    this.errorMessage.set(null);
    this.saving.set(true);

    try {
      await this.api.uploadAttachment(this.workspace.selectedWorkspace().id, uuid, file, data);
      await this.loadOne(uuid);
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.saving.set(false);
    }
  }

  private async save(operation: () => Promise<RepairOrder>): Promise<RepairOrder> {
    this.errorMessage.set(null);
    this.saving.set(true);

    try {
      const repair = await operation();
      this.setRepair(repair);
      return repair;
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.saving.set(false);
    }
  }

  private setRepair(repair: RepairOrder): void {
    this.selectedRepair.set(repair);
    this.repairs.update((items) => [repair, ...items.filter((item) => item.uuid !== repair.uuid)]);
  }

  private errorFrom(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) return 'No fue posible completar la operacion.';

    const errors = error.error?.errors as Record<string, string[]> | undefined;
    const first = errors ? Object.values(errors).flat()[0] : null;
    return first ?? error.error?.message ?? 'No fue posible completar la operacion.';
  }
}
